import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Checkbox, Skeleton } from '../../../../ds/components';
import { useIsMobile } from '../../../../ds/hooks';
import { errorMessage } from '../../../../lib/api';
import { queryRoots } from '../../../../lib/events';
import { sessionQueryKey, useSession } from '../../../../lib/session';
import { RecoveryCodes } from '../../../auth/RecoveryCodes';
import { useTwoFactorSetup } from '../../../auth/TwoFactorSetupPanel';
import { CodeInput, WizardButton } from '../../ui';
import { useFinishSetup, useStepAction } from '../api';
import { StepFrame, useWizard } from '../StepFrame';
import { Panel } from '../parts';
import styles from './SecurityStep.module.css';

/** Shown instead of the mandatory hints while the server does not require 2FA (`TWO_FACTOR_MODE=optional`, D-338). */
export const OPTIONAL_TWO_FACTOR_TEXT =
  'Optional — Sie können die Zwei-Faktor-Anmeldung später unter Einstellungen › Sicherheit einrichten.';

/**
 * Nutzer 11 / M9: the 2FA. `POST /auth/2fa/confirm` completes the step and turns the setup session into a full one;
 * the ten recovery codes follow, then the wizard is finished (`POST /setup/user/finish`). Mandatory (no skip) while
 * the server requires 2FA; with `twoFactorRequired = false` (D-338) „Später einrichten" skips the step and finishes.
 */
export function SecurityStep() {
  const mobile = useIsMobile();
  const wizard = useWizard();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  // A full session that re-enters the wizard (old tab, bookmark, checklist) already has its 2FA: no new secret.
  const alreadyEnabled = session.account.twoFactorEnabled;
  const twoFactor = useTwoFactorSetup({ enabled: !alreadyEnabled });
  const finish = useFinishSetup();
  const skipStep = useStepAction();
  const optional = !session.twoFactorRequired;
  const [saved, setSaved] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);
  const { setup, code, setCode, codes, confirm, fieldError, attempts } = twoFactor;

  async function done(target: string) {
    setFinishError(null);
    try {
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      await finish.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: queryRoots.setup });
      await navigate(target, { replace: true });
    } catch (failure) {
      setFinishError(errorMessage(failure));
    }
  }

  async function skip(target: string) {
    setFinishError(null);
    try {
      await skipStep.mutateAsync({ key: 'security', action: 'skip' });
    } catch (failure) {
      setFinishError(errorMessage(failure));
      return;
    }
    await done(target);
  }
  const skipping = skipStep.isPending || (finish.isPending && !codes);

  const submit = () => {
    if (code.length === 6 && !confirm.isPending) confirm.mutate(code);
  };
  const back = wizard.goBack ? { label: 'Zurück', onClick: wizard.goBack } : undefined;
  const skipped = wizard.setup.summary.openLabels.filter((label) => label !== 'Zwei-Faktor');

  if (alreadyEnabled) {
    const activeText =
      'Die Zwei-Faktor-Anmeldung ist für dieses Konto bereits eingerichtet. Es ist nichts weiter zu tun.';
    if (mobile) {
      return (
        <StepFrame title="Zwei-Faktor einrichten" intro={false} width={390} footer={null} mobileFooter={null}>
          <Panel className={styles.mobileDone}>
            <span className={styles.doneMark} aria-hidden="true">
              ✓
            </span>
            <span className={styles.mobileDoneTitle}>Zweiter Faktor ist aktiv</span>
            <span className={styles.mobileDoneText}>{activeText}</span>
            {finishError ? <span className={styles.error}>{finishError}</span> : null}
            <WizardButton
              variant="primary"
              size="mobile"
              block
              loading={finish.isPending}
              onClick={() => void done('/tasks')}
            >
              DAMOKLES öffnen
            </WizardButton>
          </Panel>
        </StepFrame>
      );
    }
    return (
      <StepFrame
        title="Zweiter Faktor einrichten"
        text={`${wizard.setup.user.organizationName} verlangt die Zwei-Faktor-Anmeldung.`}
        width={760}
        footer={{
          back,
          hint: finishError ? <span className={styles.error}>{finishError}</span> : 'Bereits erledigt',
          primary: {
            label: 'Weiter',
            onClick: () => void done('/setup/user/done'),
            loading: finish.isPending,
          },
        }}
      >
        <Panel className={styles.card}>
          <div className={styles.info}>✓ {activeText}</div>
        </Panel>
      </StepFrame>
    );
  }

  if (codes && !mobile) {
    return (
      <StepFrame
        title="Wiederherstellungscodes"
        text="Zehn Codes, jeder gilt einmal. Bewahren Sie sie außerhalb des Handys auf — sie ersetzen die App, wenn Sie keinen Zugriff darauf haben."
        width={760}
        footer={{
          hint: finishError ? <span className={styles.error}>{finishError}</span> : undefined,
          primary: {
            label: 'Weiter',
            disabled: !saved,
            loading: finish.isPending,
            onClick: () => void done('/setup/user/done'),
          },
        }}
      >
        <Panel className={styles.codesCard}>
          <RecoveryCodes codes={codes} />
          <Checkbox
            label="Ich habe die Codes kopiert oder gespeichert."
            checked={saved}
            onChange={(event) => setSaved(event.target.checked)}
          />
        </Panel>
      </StepFrame>
    );
  }

  const qr = (
    <div className={styles.qrColumn}>
      <div className={styles.qrFrame}>
        {setup.data ? (
          <img className={styles.qr} src={setup.data.qrDataUrl} alt="QR-Code für die Authenticator-App" />
        ) : (
          <Skeleton variant="block" width="100%" height="100%" />
        )}
      </div>
      {mobile ? null : (
        <span className={styles.key}>
          Schlüssel manuell:
          <br />
          <span className={styles.keyGroups}>{setup.data ? setup.data.keyGroups.join(' · ') : ''}</span>
        </span>
      )}
    </div>
  );
  const input = (
    <CodeInput
      label="Sechsstelliger Code aus der Authenticator-App"
      value={code}
      onChange={setCode}
      onComplete={(value) => confirm.mutate(value)}
      error={fieldError ? (attempts ? `${fieldError} ${attempts}` : fieldError) : undefined}
      disabled={confirm.isPending || !setup.data || codes !== null}
      autoFocus={!mobile}
    />
  );

  if (mobile) {
    return (
      <StepFrame title="Zwei-Faktor einrichten" intro={false} width={390} footer={null} mobileFooter={null}>
        {codes ? (
          <Panel className={styles.mobileCard}>
            <span className={styles.mobileCardTitle}>Wiederherstellungscodes</span>
            <RecoveryCodes codes={codes} />
            <Checkbox
              label="Ich habe die Codes kopiert oder gespeichert."
              checked={saved}
              onChange={(event) => setSaved(event.target.checked)}
            />
          </Panel>
        ) : (
          <Panel className={styles.mobileCard}>
            <span className={styles.mobileCardTitle}>Zwei-Faktor einrichten</span>
            {qr}
            {setup.data ? <span className={styles.keyMobile}>{setup.data.keyGroups.join(' · ')}</span> : null}
            {input}
            {setup.isError ? <span className={styles.error}>{errorMessage(setup.error)}</span> : null}
            <span className={styles.mandatory}>
              {optional ? OPTIONAL_TWO_FACTOR_TEXT : 'Pflicht der Organisation · nicht überspringbar'}
            </span>
            {optional ? (
              <WizardButton
                variant="ghost"
                size="mobile"
                block
                loading={skipping}
                onClick={() => void skip('/tasks')}
              >
                Später einrichten
              </WizardButton>
            ) : null}
          </Panel>
        )}
        <Panel className={styles.mobileDone}>
          <span className={styles.doneMark} aria-hidden="true">
            ✓
          </span>
          <span className={styles.mobileDoneTitle}>Danach können Sie starten</span>
          <span className={styles.mobileDoneText}>
            {skipped.length > 0
              ? `Übersprungene Schritte landen als Checkliste in der Übersicht: ${skipped.join(', ')}.`
              : 'Alle Schritte sind erledigt.'}
          </span>
          {finishError ? <span className={styles.error}>{finishError}</span> : null}
          <WizardButton
            variant="primary"
            size="mobile"
            block
            disabled={!codes || !saved}
            loading={finish.isPending}
            onClick={() => void done('/tasks')}
          >
            DAMOKLES öffnen
          </WizardButton>
        </Panel>
      </StepFrame>
    );
  }

  return (
    <StepFrame
      title="Zweiter Faktor einrichten"
      text={
        optional
          ? OPTIONAL_TWO_FACTOR_TEXT
          : `${wizard.setup.user.organizationName} verlangt die Zwei-Faktor-Anmeldung. Dieser Schritt lässt sich nicht überspringen.`
      }
      width={760}
      footer={{
        back,
        ...(optional
          ? {
              hint: finishError ? <span className={styles.error}>{finishError}</span> : undefined,
              secondary: {
                label: 'Später einrichten',
                onClick: () => void skip('/setup/user/done'),
                loading: skipping,
              },
            }
          : { hint: 'Pflichtschritt · kein Überspringen möglich' }),
        primary: {
          label: 'Bestätigen',
          onClick: submit,
          disabled: code.length < 6 || !setup.data,
          loading: confirm.isPending,
        },
      }}
    >
      <Panel className={styles.card}>
        {qr}
        <div className={styles.right}>
          <div className={styles.instruction}>
            <span className={styles.instructionTitle}>1. Authenticator-App öffnen und Code scannen</span>
            <span className={styles.instructionText}>
              Google Authenticator, Microsoft Authenticator, 1Password oder jede TOTP-App.
            </span>
          </div>
          <div className={styles.instruction}>
            <span className={styles.instructionTitle}>2. Sechsstelligen Code eingeben</span>
            {input}
          </div>
          {setup.isError ? <span className={styles.error}>{errorMessage(setup.error)}</span> : null}
          <div className={styles.info}>
            Nach der Bestätigung erhalten Sie zehn Wiederherstellungscodes zum Ausdrucken.
          </div>
        </div>
      </Panel>
    </StepFrame>
  );
}
